import { GiPathDistance } from "react-icons/gi";
import {
  ConditionalPropertyCallbackContext,
  defineField,
  defineType,
  SchemaValidationValue
} from "sanity";
import { multilingualStringPreview } from "../base/multilingualArrayString";
import { cruiseGroups } from "../data-types/cruiseGroups";
import { fareCodes } from "../data-types/faresTokens";

const trendingModelEnabled = (context: ConditionalPropertyCallbackContext) => {
  return context.parent?.modelType === "trending";
};

const validateSingleElementForTrendingModel: SchemaValidationValue = (rule) =>
  rule.custom((value, context) => {
    const isTrendingModel = (context?.parent as any)?.modelType === "trending";
    if (isTrendingModel && Array.isArray(value) && value.length > 1) {
      return "Only one destination is allowed for the trending model";
    }
    return true;
  });

export default defineType({
  title: "Cruise Slider",
  description: "Shows up to 20 cruise cards",
  name: "cruiseSlider",
  type: "object",
  icon: GiPathDistance,
  fieldsets: [
    {
      name: "filters",
      title: "Set filters",
      options: { collapsible: true, collapsed: true }
    }
  ],
  fields: [
    defineField({
      name: "modelType",
      type: "string",
      initialValue: "standard",
      options: {
        layout: "radio",
        direction: "horizontal",
        list: [
          { title: "Standard (default)", value: "standard" },
          { title: "Trending (AI)", value: "trending" }
        ]
      }
    }),
    {
      name: "sectionLayoutConfig",
      type: "sectionLayoutConfig",
      title: "Section Layout",
      options: {
        collapsed: true,
        collapsable: true
      }
    },
    {
      name: "heading",
      title: "Heading",
      type: "sectionTitle"
    },
    {
      name: "footerNote",
      title: "Footer Note",
      description: "leave empty for default",
      type: "multilingualArrayText",
      options: {
        collapsed: true,
        collapsable: true
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByShip",
      title: "Filter by Ship",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "ship" }]
        }
      ]
    },
    {
      fieldset: "filters",
      name: "filterByDestination",
      title: "Filter by Destination",
      type: "array",
      validation: validateSingleElementForTrendingModel,
      of: [
        {
          type: "reference",
          to: [{ type: "destination" }]
        }
      ]
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCruiseGroups",
      title: "Filter by Cruise Groups",
      type: "array",
      of: [
        {
          type: "string"
        }
      ],
      options: {
        list: cruiseGroups
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByFareCodes",
      title: "Filter by Fares",
      type: "array",
      of: [
        {
          type: "string"
        }
      ],
      options: {
        list: fareCodes
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByOffer",
      title: "Filter by Offer",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "exclusiveOfferPerPromo" }]
        }
      ]
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCruiseType",
      title: "Filter by Cruise Type",
      description: "Leave empty for all",
      type: "array",
      of: [{ type: "string" }],
      options: {
        list: [
          { title: "Classic", value: "Classic" },
          { title: "Expedition", value: "Expedition" }
        ]
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByComboType",
      title: "Filter by Combo Type",
      description: "Leave empty for all",
      type: "array",
      of: [{ type: "string" }],
      options: {
        list: [
          { title: "Single", value: "Single" },
          { title: "Combo", value: "Combo" },
          { title: "World", value: "World" },
          { title: "Grand", value: "Grand" }
        ]
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "cruises",
      title: "Filter by Cruise",
      description: "Up to 20 items will be shown",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "cruise" }, { type: "specialVoyage" }]
        }
      ],
      validation: (Rule) => Rule.max(20)
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCruiseCodes",
      title: "Filter by Cruise Codes",
      description: "comma separated cruise codes",
      type: "text"
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCruisesLaunch",
      title: "Filter by Cruises Launch",
      type: "reference",
      to: [{ type: "cruisesLaunch" }],
      weak: true
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCountries",
      title: "Filter by Countries",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "country" }]
        }
      ]
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByPorts",
      title: "Filter by Ports",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "port" }]
        }
      ]
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByFeatures",
      title: "Filter by Features",
      description: "Leave empty for all",
      type: "array",
      of: [{ type: "string" }],
      options: {
        list: [
          { title: "Bridge Sailings", value: "BridgeSailings" },
          { title: "Gentlemen Hosts", value: "GentlemenHosts" },
          { title: "Culinary Voyages", value: "CulinaryVoyages" },
          { title: "Wine Cruises", value: "WineCruises" }
        ]
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByCruiseTagCategories",
      title: "Filter by Cruise Tag Categories",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "cruisesTagCategory" }]
        }
      ]
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "filterByLoadStress",
      title: "Filter by Load Stress",
      type: "array",
      of: [{ type: "string" }],
      options: {
        list: ["Good", "Average", "Bad", "Last Minute", "Distress"]
      }
    },
    {
      fieldset: "filters",
      hidden: trendingModelEnabled,
      name: "sortBy",
      title: "Sort by",
      type: "string",
      options: {
        list: [
          {
            title: "default",
            value: null
          },
          {
            title: "Price (low -> high)",
            value: "price-asc"
          },
          {
            title: "Price (high -> low)",
            value: "price-desc"
          },
          {
            title: "Departure date",
            value: "departure-asc"
          },
          {
            title: "Duration (min to max)",
            value: "duration-asc"
          },
          {
            title: "Duration (max to min)",
            value: "duration-desc"
          }
        ]
      }
    }
  ],
  preview: {
    select: {
      title: "heading.title",
      modelType: "modelType"
    },
    prepare(selection) {
      const { title, modelType } = selection;
      return {
        title: multilingualStringPreview(title) || "([Cruise] Slider)",
        subtitle: `Cruise Slider ${modelType === "trending" ? "(trending)" : ""}`
      };
    }
  }
});
